import React, { useState, useContext, useEffect } from "react";
import { Button } from "components/ui";
import { apiRoute } from "services";
import API from "services/lib/api";
import { dataQueryStatus } from "utils";
import { getErrorMessage } from "utils/helper";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";
import TicketDistribution from "./TicketDistribution/TicketDistribution.js.js";
import TicketSchedule from "./TicketSchedule/TicketSchedule";
import ComponentAccessControl from "components/essentials/ComponentAccessControl/ComponentAccessControl.js";
import { baseResources, baseResourceActions } from "routes/enum/index.js";
import "./CustomRules.scss";

const { AGENTS_INBOX_BASE_SETTING } = baseResources;
const { UPDATE } = baseResourceActions;
const { ERROR } = dataQueryStatus;

const CustomRules = ({ customRules, teamId, handleSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [request, setRequest] = useState({
        ticketDistributionRule: customRules?.ticketDistributionRule || "",
        weekDayScheduleStartTime: customRules?.weekDayScheduleStartTime || "",
        weekDayScheduleEndTime: customRules?.weekDayScheduleEndTime || "",
        weekendScheduleStartTime: customRules?.weekendScheduleStartTime || "",
        weekendScheduleEndTime: customRules?.weekendScheduleEndTime || "",
        customSchedules: customRules?.customSchedules || [],
    });
    const toastMessage = useContext(ToastContext);
    const [disabledBtn, setDisabledBtn] = useState(true);

    // Update request state when customRules prop changes (e.g., after data fetch)
    useEffect(() => {
        if (customRules) {
            setRequest({
                ticketDistributionRule: customRules?.ticketDistributionRule || "",
                weekDayScheduleStartTime: customRules?.weekDayScheduleStartTime || "",
                weekDayScheduleEndTime: customRules?.weekDayScheduleEndTime || "",
                weekendScheduleStartTime: customRules?.weekendScheduleStartTime || "",
                weekendScheduleEndTime: customRules?.weekendScheduleEndTime || "",
                customSchedules: customRules?.customSchedules || [],
            });
        }
    }, [customRules]);

    const handleRequestValues = (type, value) => {
        setRequest((prevRequest) => {
            switch (type) {
                case "ticketDistributionRule":
                    return { ...prevRequest, ticketDistributionRule: value };
                case "weekDayScheduleStartTime":
                    return { ...prevRequest, weekDayScheduleStartTime: value };
                case "weekDayScheduleEndTime":
                    return { ...prevRequest, weekDayScheduleEndTime: value };
                case "weekendScheduleStartTime":
                    return { ...prevRequest, weekendScheduleStartTime: value };
                case "weekendScheduleEndTime":
                    return { ...prevRequest, weekendScheduleEndTime: value };
                case "customSchedules":
                    return { ...prevRequest, customSchedules: Array.isArray(value) ? value : [] };
                default:
                    return prevRequest;
            }
        });
    };

    const updateCustomRules = async () => {
        try {
            setLoading(true);
            // Ensure customSchedules is always included and properly formatted
            // Filter out any invalid entries and ensure proper structure
            const processedCustomSchedules = Array.isArray(request.customSchedules) 
                ? request.customSchedules
                    .filter(schedule => schedule && schedule.dayType) // Only include schedules with dayType
                    .map(schedule => ({
                        id: schedule.id || undefined,
                        dayType: schedule.dayType || "",
                        startTime: schedule.startTime || "",
                        endTime: schedule.endTime || "",
                    }))
                : [];
            
            const requestPayload = {
                ticketDistributionRule: request.ticketDistributionRule,
                weekDayScheduleStartTime: request.weekDayScheduleStartTime || null,
                weekDayScheduleEndTime: request.weekDayScheduleEndTime || null,
                weekendScheduleStartTime: request.weekendScheduleStartTime || null,
                weekendScheduleEndTime: request.weekendScheduleEndTime || null,
                customSchedules: processedCustomSchedules,
            };
            
            const res = await API.put(apiRoute?.customRule(teamId), requestPayload);
            
            if (res.status === 200) {
                const { message } = res.data;
                
                toastMessage(message);
                handleSuccess();
            }

            setLoading(false);
        } catch (err) {
            setLoading(false);
            const message = getErrorMessage(err);
            toastMessage(message, ERROR);
        }
    };

    const validateRequest = () => {
        // Validate ticket distribution rule
        if (!request.ticketDistributionRule || request.ticketDistributionRule.trim().length === 0) {
            return { isValid: false };
        }
        
        // Validate at least one schedule has both start and end times
        const hasValidWeekdaySchedule = 
            request.weekDayScheduleStartTime && 
            request.weekDayScheduleEndTime &&
            request.weekDayScheduleStartTime.trim().length > 0 &&
            request.weekDayScheduleEndTime.trim().length > 0;
        
        const hasValidWeekendSchedule = 
            request.weekendScheduleStartTime && 
            request.weekendScheduleEndTime &&
            request.weekendScheduleStartTime.trim().length > 0 &&
            request.weekendScheduleEndTime.trim().length > 0;
        
        // Handle customSchedules - treat undefined/null as empty array
        const customSchedules = request.customSchedules || [];
        const hasValidCustomSchedules = 
            Array.isArray(customSchedules) &&
            customSchedules.length > 0 &&
            customSchedules.some(
                schedule => 
                    schedule && 
                    schedule.startTime && 
                    schedule.endTime &&
                    schedule.startTime.trim().length > 0 &&
                    schedule.endTime.trim().length > 0
            );
        
        // At least one schedule must be valid
        if (!hasValidWeekdaySchedule && !hasValidWeekendSchedule && !hasValidCustomSchedules) {
            return { isValid: false };
        }
        
        return { isValid: true };
    };

    useEffect(() => {
        const { isValid } = validateRequest();
        isValid ? setDisabledBtn(false) : setDisabledBtn(true);
        //eslint-disable-next-line
    }, [request]);

    return (
        <>
            <div className='row'>
                <div className='custom-rules col-12'>
                    <div>
                        <div className='custom-rules--header'>
                            <h1 className='custom-rules--header--title fw-bold'>
                                Create custom rules
                            </h1>
                            <p className='custom-rules--header--sub__title'>
                                Configure how tickets are distributed among team members and set business hours 
                                to control when tickets are assigned. You can create custom schedules for specific 
                                days of the week or use default weekday and weekend schedules.
                            </p>
                        </div>
                        <ComponentAccessControl
                            baseResources={[
                                `${UPDATE}_${AGENTS_INBOX_BASE_SETTING}`,
                            ]}>
                            <TicketDistribution
                                request={request}
                                handleRequestValues={handleRequestValues}
                            />
                        </ComponentAccessControl>
                        <ComponentAccessControl
                            baseResources={[
                                `${UPDATE}_${AGENTS_INBOX_BASE_SETTING}`,
                            ]}>
                            <TicketSchedule
                                request={request}
                                handleRequestValues={handleRequestValues}
                            />
                        </ComponentAccessControl>
                    </div>
                    <ComponentAccessControl
                        baseResources={[
                            `${UPDATE}_${AGENTS_INBOX_BASE_SETTING}`,
                        ]}>
                        <Button
                            type='submit'
                            text='Save Changes'
                            classType='primary'
                            otherClass='my-3 w-lg-25 w-md-50 w-sm-100'
                            loading={loading}
                            disabled={disabledBtn}
                            onClick={updateCustomRules}
                        />
                    </ComponentAccessControl>
                </div>
            </div>
        </>
    );
};

export default CustomRules;
